import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '@/lib/api';
import type {
  EmailChangeInput,
  LoginInput,
  PasswordChangeInput,
  PreferencesInput,
  User,
} from './types';

const ME_KEY = ['auth', 'me'] as const;

/**
 * Current user, or null when signed out.
 *
 * A 401 is a valid answer here, not an error -- retrying it would just burn
 * requests on every page load while logged out.
 */
export function useCurrentUser() {
  return useQuery<User | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api<User>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, LoginInput>({
    mutationFn: (input) => api<User>('/api/auth/login', { method: 'POST', json: input }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      // Everything cached belongs to the session that just ended.
      qc.clear();
    },
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, PreferencesInput>({
    mutationFn: (input) =>
      api<User>('/api/auth/me', { method: 'PATCH', json: input }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

export function useChangeEmail() {
  const qc = useQueryClient();
  return useMutation<User, ApiError, EmailChangeInput>({
    mutationFn: (input) =>
      api<User>('/api/auth/email', { method: 'POST', json: input }),
    onSuccess: (user) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

/**
 * Change the password.
 *
 * The server kills every session including this one, so success means signed
 * out -- the cache is dropped to match rather than left showing data the next
 * request will 401 on.
 */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, PasswordChangeInput>({
    mutationFn: (input) =>
      api<void>('/api/auth/password', { method: 'POST', json: input }),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.clear();
    },
  });
}
